/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Dynamic Theme Provider & Token Consumer (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import type { ThemeColorTokens } from '@sofia/shared-types';
import { getAdminDomains } from '../lib/urls';

export type ThemeMode = 'light' | 'dark' | 'system';
export type FontScale = 'standard' | 'comfortable' | 'large';

export interface AdminBrandingConfig {
  siteName: string;
  logoLightUrl?: string;
  logoDarkUrl?: string;
  logoCollapsedUrl?: string;
  faviconUrl?: string;
}

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  fontScale: FontScale;
  setFontScale: (scale: FontScale) => void;
  isDark: boolean;
  branding: AdminBrandingConfig | null;
  refreshBranding: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_MODE_STORAGE_KEY = 'sophia_admin_theme_mode';
const THEME_TOKENS_STORAGE_KEY = 'sophia_cached_admin_theme_tokens';
const FONT_SCALE_STORAGE_KEY = 'sophia_font_scale';

// Default Fallback Color Tokens (Electric Purple / Neon Cyan)
const DEFAULT_FALLBACK_TOKENS: { light: ThemeColorTokens; dark: ThemeColorTokens } = {
  light: {
    primary: '262 83% 58%',
    primaryHover: '262 83% 52%',
    primaryFg: '0 0% 100%',
    primary50: '262 100% 97%',
    primary100: '262 93% 94%',
    accent: '199 89% 48%',
    accentHover: '199 89% 42%',
    accentFg: '0 0% 100%',
    surface: '0 0% 100%',
    surfaceElevated: '0 0% 98%',
    border: '220 13% 91%',
    bgBase: '210 40% 98%',
    textMain: '222 47% 11%',
    textMuted: '215 16% 47%',
    muted: '210 40% 96%',
  },
  dark: {
    primary: '262 83% 58%',
    primaryHover: '262 83% 64%',
    primaryFg: '0 0% 100%',
    primary50: '262 50% 12%',
    primary100: '262 50% 18%',
    accent: '199 89% 48%',
    accentHover: '199 89% 54%',
    accentFg: '0 0% 100%',
    surface: '224 71% 4%',
    surfaceElevated: '224 71% 7%',
    border: '217 33% 17%',
    bgBase: '222 47% 11%',
    textMain: '210 40% 98%',
    textMuted: '215 20% 65%',
    muted: '217 33% 17%',
  },
};

function applyCssTokens(tokens: ThemeColorTokens): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  root.style.setProperty('--color-primary', tokens.primary);
  root.style.setProperty('--color-primary-hover', tokens.primaryHover);
  root.style.setProperty('--color-primary-fg', tokens.primaryFg);
  if (tokens.primary50) root.style.setProperty('--color-primary-50', tokens.primary50);
  if (tokens.primary100) root.style.setProperty('--color-primary-100', tokens.primary100);
  root.style.setProperty('--color-accent', tokens.accent);
  root.style.setProperty('--color-accent-hover', tokens.accentHover);
  root.style.setProperty('--color-accent-fg', tokens.accentFg);
  root.style.setProperty('--color-surface', tokens.surface);
  root.style.setProperty('--color-surface-elevated', tokens.surfaceElevated);
  root.style.setProperty('--color-border', tokens.border);
  root.style.setProperty('--color-bg-base', tokens.bgBase);
  root.style.setProperty('--color-text-main', tokens.textMain);
  root.style.setProperty('--color-text-muted', tokens.textMuted);
  root.style.setProperty('--color-muted', tokens.muted);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode) || 'system';
  });

  // Initialize ergonomic font scale preference
  const [fontScale, setFontScaleState] = useState<FontScale>(() => {
    if (typeof window === 'undefined') return 'standard';
    return (localStorage.getItem(FONT_SCALE_STORAGE_KEY) as FontScale) || 'standard';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-font-scale', fontScale);
  }, [fontScale]);

  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const isDark = useMemo(() => {
    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;
    return systemPrefersDark;
  }, [themeMode, systemPrefersDark]);

  const [branding, setBranding] = useState<AdminBrandingConfig | null>(() => {
    try {
      const cached = localStorage.getItem('sophia_cached_branding');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const refreshBranding = useCallback(async () => {
    try {
      const domains = getAdminDomains();
      const res = await fetch(`${domains.apiOrigin}/api/v1/public/theme`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const json = await res.json() as any;
        const b = json?.data?.branding;
        if (b && typeof b === 'object') {
          const brandData: AdminBrandingConfig = {
            siteName: b.siteName || 'Sophia Platform',
            logoLightUrl: b.logoLightUrl,
            logoDarkUrl: b.logoDarkUrl,
            logoCollapsedUrl: b.logoCollapsedUrl,
            faviconUrl: b.faviconUrl,
          };
          setBranding(brandData);
          try {
            localStorage.setItem('sophia_cached_branding', JSON.stringify(brandData));
          } catch {}

          // Synchronize favicon
          if (brandData.faviconUrl && typeof document !== 'undefined') {
            let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = brandData.faviconUrl;
          }
        }
      }
    } catch (err) {
      console.warn('[Admin ThemeProvider] Failed to fetch branding:', err);
    }
  }, []);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    const tokens = isDark ? DEFAULT_FALLBACK_TOKENS.dark : DEFAULT_FALLBACK_TOKENS.light;
    applyCssTokens(tokens);

    try {
      localStorage.setItem(THEME_TOKENS_STORAGE_KEY, JSON.stringify(DEFAULT_FALLBACK_TOKENS));
    } catch {
      // Ignore localStorage quotas
    }
  }, [isDark]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      // Ignore
    }
  };

  const setFontScale = (scale: FontScale) => {
    setFontScaleState(scale);
    try {
      localStorage.setItem(FONT_SCALE_STORAGE_KEY, scale);
    } catch {
      // Ignore
    }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-font-scale', scale);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        setThemeMode,
        fontScale,
        setFontScale,
        isDark,
        branding,
        refreshBranding,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
